import React, { useEffect, useMemo, useState } from 'react'
import { sentenceCase } from 'formatters'
import { makeStyles } from '@material-ui/core'
import { SlideTransition } from 'functional-foundations'
import { Textarea, Typography, Checkbox } from 'foundations-library/components'
import { getPredefinedValues, getOtherUseCaseTextValue } from '../../../utils/util'
import { maxlength, minlength, required } from 'validation-rules'

const OTHER_TEXT_MIN_VALUE = 40
const OTHER_TEXT_MAX_VALUE = 4096

const jss = () => ({
    checboxesContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 15,
        marginBottom: 15,
        marginTop: 15
    },
    checkboxesLabel: {
        marginBottom: 15
    },
    textarea: {
        '& textarea': {
            minHeight: 150
        }
    }
})

const useStyles = makeStyles(jss)

interface PropsInterface {
    value: string[]
    onChange(v: string[]): unknown
    disabled?: boolean
    isValid(v: boolean): unknown
}

/**
 *
 */
const UseCasesCheckboxes = ({
    value = [],
    onChange,
    disabled = false,
    isValid
}: PropsInterface): JSX.Element => {
    const styles = useStyles()
    const [isDataDirty, setIsDataDirty] = useState(false)
    const [oneWordOptions, setOneWordOptions] = useState([])

    const predefinedValues = useMemo(() => {
        return getPredefinedValues(value)
    }, [value])

    const [isOtherChecked, setIsOtherChecked] = useState(false)
    const [otherTextValue, setOtherTextValue] = useState('')

    useEffect(() => {
        const otherText = getOtherUseCaseTextValue(value)
        setIsOtherChecked(!!otherText)
        setOtherTextValue(otherText || '')

        const predefinedCheckboxValues = predefinedValues.map((x) => x.value)

        const unclassifiedOptions = value.filter((x) => x && !predefinedCheckboxValues.includes(x) && x !== otherText)
        setOneWordOptions(unclassifiedOptions.map((x) => ({
            value: x,
            label: sentenceCase(x)
        })))
    }, [])

    const emitChanges = (v: string[]) => {
        if (!isDataDirty) {
            setIsDataDirty(JSON.stringify(value) !== JSON.stringify(v))
        }
        const newValues = [...new Set(v.filter((x) => x))]
        onChange?.(newValues)
        isValid(
            !!newValues.length &&
            (isOtherChecked ? (!!otherTextValue && otherTextValue.length >= OTHER_TEXT_MIN_VALUE && otherTextValue.length <= OTHER_TEXT_MAX_VALUE) : true)
        )
    }

    const onCheckboxChange = (val, checked) => {
        const newValueArray = checked ? [...value, val] : value.filter((x) => x !== val)
        if (newValueArray.length > 5) {
            return null
        }

        emitChanges(newValueArray)
    }

    useEffect(() => {
        let newValueArray = [...value]
        if (isOtherChecked) {
            if (otherTextValue) newValueArray.push(otherTextValue)
        } else {
            newValueArray = newValueArray.filter((x) => x !== otherTextValue)
        }
        emitChanges(newValueArray)
    }, [isOtherChecked])

    const onOtherTextValueChange = (v) => {
        if (isOtherChecked) {
            const newValues = [...value.filter((x) => x !== otherTextValue)]
            newValues.push(v)
            emitChanges(newValues)
            setOtherTextValue(v)
        }
    }

    const onOtherCheckboxValueChange = (v) => {
        if (v && (value?.length || 0) >= 5) return null
        setIsOtherChecked(v)
    }

    const isOtherDisabled = disabled || (value.length >= 5 && !value.includes(otherTextValue))

    return <div data-testid='usecases'>
        <div className={styles.checboxesContainer}>
            {
                [...predefinedValues, ...oneWordOptions].map(
                    (cb, index) => (
                        <Checkbox
                            label={cb.label}
                            value={cb.value}
                            onChange={(v) => onCheckboxChange(cb.value, !!v)}
                            checked={value.includes(cb.value)}
                            key={cb.label}
                            disabled={
                                disabled ||
                                (
                                    (
                                        value.length >= 5 ||
                                        (
                                            value.filter((x) => x !== otherTextValue).length === 4 &&
                                            isOtherChecked
                                        )
                                    ) && !value.includes(cb.value)
                                )
                            }
                            data-testid={`usecases-${index}`}
                        />
                    )
                )}
            <Checkbox
                label='Other (please specify)'
                value={true}
                checked={isOtherChecked}
                onChange={onOtherCheckboxValueChange}
                disabled={isOtherDisabled}
                data-testid='usecases-cb-other'
            />
            {
                isDataDirty && !value.length && <Typography variant='helperText' color='destructive-500' data-testid='validation-errors'>
                    This field is requried
                </Typography>
            }
        </div>
        <SlideTransition isOpen={isOtherChecked} direction='up'>
            <Textarea
                value={otherTextValue}
                onChange={onOtherTextValueChange}
                placeholder='Enter text here'
                className={styles.textarea}
                disabled={!isOtherChecked || disabled || isOtherDisabled}
                rules={[required(), minlength(OTHER_TEXT_MIN_VALUE), maxlength(OTHER_TEXT_MAX_VALUE)]}
                data-testid='usecases-other-textarea'
            />
        </SlideTransition>
    </div>
}

export default UseCasesCheckboxes
